import React from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography, Button, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import CustomTypography from 'components/Fileds/CustomTypography';

const ViewQrModal = ({ open, onClose, table }) => {
    const theme = useTheme();
    if (!table) return null;

    const isVendor = table?.isVendor || table?.table_no === 'Vendor';
    const qrTitle = table?.title || (isVendor ? "Vendor QR Code" : `Table ${table?.table_no} QR Code`);
    const qrSubtitle = table?.subtitle || (isVendor ? "Scan to view menu & order" : `Unique QR for Table ${table?.table_no}. Customers can scan to view the menu.`);
    const displayLabel = table?.displayLabel || (isVendor ? "Vendor QR Code" : table?.table_no);
    const downloadFileName = table?.fileName || (isVendor ? "Vendor_QR.png" : `${table?.table_no}_QR.png`);

    const handleDownload = async () => {
        if (!table?.qr_code_url) return;
        try {
            const response = await fetch(table.qr_code_url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = downloadFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Error downloading QR image:", error);
            // Fallback download method
            const link = document.createElement('a');
            link.href = table.qr_code_url;
            link.download = downloadFileName;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handlePrint = () => {
        if (!table?.qr_code_url) return;
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Print QR Code - ${displayLabel}</title>
                    <style>
                        @page {
                            size: auto;
                            margin: 0;
                        }
                        * {
                            box-sizing: border-box;
                        }
                        html, body {
                            margin: 0;
                            padding: 0;
                            width: 100%;
                            background-color: #fff;
                            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                        }
                        body {
                            display: flex;
                            align-items: flex-start;
                            justify-content: center;
                        }
                        .print-container {
                            width: 80mm;
                            max-width: 100%;
                            padding: 5px 10px 15px 10px;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            text-align: center;
                            margin: 0 auto;
                        }
                        .divider {
                            width: 100%;
                            border-top: 1.5px solid #000 !important;
                            margin: 12px 0;
                            display: block;
                        }
                        .title {
                            font-size: 20px;
                            font-weight: 800;
                            color: #000;
                            margin-bottom: 4px;
                            text-transform: capitalize;
                        }
                        .subtitle {
                            font-size: 12px;
                            color: #555;
                            margin-bottom: 8px;
                        }
                        .qr-img {
                            width: 200px;
                            height: 200px;
                            object-fit: contain;
                            display: block;
                            margin: 0 auto;
                        }
                        .bottom-text {
                            font-size: 14px;
                            font-weight: 700;
                            color: #000;
                            margin-top: 8px;
                        }
                    </style>
                </head>
                <body>
                    <div class="print-container">
                        <div class="divider"></div>
                        <div class="title">${displayLabel}</div>
                        <div class="subtitle">Scan to view menu & order</div>
                        <img id="qr-image" src="${table.qr_code_url}" class="qr-img" />
                        <div class="bottom-text">${displayLabel}</div>
                        <div class="divider"></div>
                    </div>
                    <script>
                        const img = document.getElementById('qr-image');
                        const doPrint = () => {
                            setTimeout(() => {
                                window.print();
                                window.close();
                            }, 250);
                        };
                        if (img.complete) {
                            doPrint();
                        } else {
                            img.onload = doPrint;
                            img.onerror = doPrint;
                        }
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    padding: '10px 15px',
                    color: 'black'
                }
            }}
        >
            {/* Header */}
            <Box className="d-flex align-items-start justify-content-between" sx={{ p: 1, pb: 0 }}>
                <Box className="d-flex align-items-start" sx={{ gap: 1 }}>
                    <QrCodeScannerIcon sx={{ fontSize: '24px', mt: '3px', color: theme.palette.typographyLabel.main }} />
                    <Box>
                        <CustomTypography
                            variant="h6"
                            label={qrTitle}
                            sx={{ fontSize: '18px', fontWeight: 700, color: theme.palette.typographyLabel.main, mb: 0.5, lineHeight: 1.2 }}
                        />
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
                            {qrSubtitle}
                        </Typography>
                    </Box>
                </Box>
                <IconButton aria-label="close" onClick={onClose} sx={{ color: '#666', p: 0.5 }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <DialogContent sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* Bordered QR Code Container */}
                <Box
                    sx={{
                        border: '1.5px solid #000',
                        borderRadius: '16px',
                        padding: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fff',
                        width: '100%',
                        maxWidth: '280px',
                        mb: 2
                    }}
                >
                    {table?.qr_code_url && (
                        <Box
                            component="img"
                            src={table.qr_code_url}
                            alt={`${displayLabel} QR Code`}
                            sx={{ width: 250, height: 250, objectFit: 'contain' }}
                        />
                    )}
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#000', fontSize: '16px' }}>
                        {displayLabel}
                    </Typography>
                </Box>
            </DialogContent>

            {/* Actions */}
            <Box className="d-flex align-items-center" sx={{ px: 2, pb: 2, gap: 1.5, justifyContent:'flex-end' }}>
                <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    sx={{
                        borderRadius: '10px',
                        borderColor: '#E0E0E0',
                        color: '#000',
                        textTransform: 'none',
                        fontWeight: 600,
                        height: '42px',
                        px: 2.5,
                        '&:hover': {
                            borderColor: '#BDBDBD',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                    }}
                >
                    Download
                </Button>
                <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    sx={{
                        borderRadius: '10px',
                        color: '#fff',
                        textTransform: 'none',
                        fontWeight: 600,
                        height: '42px',
                        px: 2.5,
                    }}
                >
                    Print
                </Button>
            </Box>
        </Dialog>
    );
};

export default ViewQrModal;
